/**
 * Sidebar (E58)
 *
 * - Dashboard (oben, fest)
 * - Kategorien als Menüpunkte (dynamisch aus ProgrammeRegistry, permission-gefiltert)
 * - Historie + Einstellungen (unten, fest)
 * - Collapsible
 * - CI: bg-brand-black, Active = bg-brand-yellow text-brand-black
 */

import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useProgrammeStore } from '../stores/programmeStore';
import { AnyPermissionGate } from './PermissionGate';

const SETTINGS_PERMISSIONS = [
  'manage:users',
  'manage:integrations',
  'manage:schedules',
  'manage:system',
  'manage:access',
  'manage:roles',
];
import { api } from '../services/api';
import { getEnvColor } from './EnvironmentBanner';

function getIcon(name: string, className?: string) {
  const Icon = (LucideIcons as any)[name];
  if (!Icon) return <LucideIcons.Box className={className || 'w-5 h-5'} />;
  return <Icon className={className || 'w-5 h-5'} />;
}

const linkBase =
  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors';
const linkActive = 'bg-brand-yellow text-brand-black';
const linkInactive = 'text-white/70 hover:text-white hover:bg-white/10';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface VersionInfo {
  backend: {
    version: string;
    gitCommit: string;
    gitCommitFull: string;
    gitBranch: string;
    gitMessage: string;
    buildTime: string;
  };
  frontend: {
    version: string;
  };
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, logout } = useAuthStore();
  const { categories } = useProgrammeStore();
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);

  useEffect(() => {
    api.get('/system/version')
      .then((res) => setVersionInfo(res.data?.data ?? null))
      .catch(() => {/* bei Fehler nichts anzeigen */});
  }, []);

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-brand-black flex flex-col z-30 transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center h-14 px-4 border-b border-white/10 shrink-0">
        <span className="font-bold text-xl shrink-0" style={{ color: getEnvColor() }}>L</span>
        {!collapsed && (
          <span className="text-white font-semibold text-lg ml-2 whitespace-nowrap">
            Libero
          </span>
        )}
      </div>

      {/* Top: Dashboard */}
      <nav className="px-2 pt-3 pb-1 shrink-0">
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `${linkBase} ${isActive ? linkActive : linkInactive}`
          }
          title="Dashboard"
        >
          <LucideIcons.LayoutDashboard className="w-5 h-5 shrink-0" />
          {!collapsed && <span>Dashboard</span>}
        </NavLink>
      </nav>

      <div className="mx-3 border-t border-white/10 my-1" />

      {/* Categories (E58: Kategorien als Menüpunkte, Klick → CategoryPage mit Karten) */}
      <nav className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
        {categories.map((cat) => (
          <NavLink
            key={cat.name}
            to={`/category/${encodeURIComponent(cat.name)}`}
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
            title={cat.name}
          >
            {getIcon(cat.icon, 'w-5 h-5 shrink-0')}
            {!collapsed && <span className="truncate">{cat.name}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="mx-3 border-t border-white/10 my-1" />

      {/* Bottom: Historie + Einstellungen */}
      <nav className="px-2 pb-2 pt-1 space-y-0.5 shrink-0">
        <NavLink
          to="/history"
          className={({ isActive }) =>
            `${linkBase} ${isActive ? linkActive : linkInactive}`
          }
          title="Historie"
        >
          <LucideIcons.History className="w-5 h-5 shrink-0" />
          {!collapsed && <span>Historie</span>}
        </NavLink>

        <AnyPermissionGate permissions={SETTINGS_PERMISSIONS}>
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
            title="Einstellungen"
          >
            <LucideIcons.Settings className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Einstellungen</span>}
          </NavLink>
        </AnyPermissionGate>
      </nav>

      {/* Versionsanzeige */}
      {versionInfo && !collapsed && (
        <div
          className="px-4 py-2 shrink-0"
          title={[
            `Version: ${versionInfo.backend.version}`,
            `Commit: ${versionInfo.backend.gitCommitFull}`,
            `Branch: ${versionInfo.backend.gitBranch}`,
            `Message: ${versionInfo.backend.gitMessage}`,
          ].join('\n')}
        >
          <p className="text-[10px] text-white/30 leading-tight font-mono">
            v{versionInfo.backend.version} · {versionInfo.backend.gitCommit}
          </p>
          <p className="text-[10px] text-white/25 leading-tight truncate">
            {versionInfo.backend.gitBranch}
          </p>
          {versionInfo.backend.buildTime && (
            <p className="text-[10px] text-white/20 leading-tight">
              Aktualisiert: {formatDate(versionInfo.backend.buildTime)}
            </p>
          )}
        </div>
      )}

      {/* User display + collapse toggle */}
      <div className="border-t border-white/10 shrink-0">
        {user && (
          <div className={`px-4 py-2 ${collapsed ? 'flex justify-center' : 'flex items-center justify-between'}`}>
            {!collapsed && (
              <span className="text-xs text-white/40 truncate">
                {user.firstName} {user.lastName}
              </span>
            )}
            <button
              onClick={() => logout()}
              title="Abmelden"
              aria-label="Abmelden"
              className="text-white/40 hover:text-white transition-colors"
            >
              <LucideIcons.LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
        <button
          onClick={onToggle}
          className="flex items-center justify-center w-full h-10 text-gray-400 hover:text-white transition-colors"
          title={collapsed ? 'Sidebar einblenden' : 'Sidebar ausblenden'}
        >
          {collapsed ? (
            <LucideIcons.ChevronRight className="w-4 h-4" />
          ) : (
            <LucideIcons.ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>
    </aside>
  );
}
